import React, { useState } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import { auth } from '../firebaseConfig'; 
import { Lock, Server, ArrowRight, Mail } from 'lucide-react'; 

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [resetMessage, setResetMessage] = useState(''); 
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setResetMessage('');
    setIsLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch (err) {
      setLoginError(err.message || "Failed to authenticate.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setLoginError("⚠️ Enter 'Work Email', then click 'Forgot Password'.");
      return;
    }
    setLoginError('');
    setResetMessage('Sending reset link... ⏳');
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setResetMessage(`✅ Reset link sent to ${email}`);
    } catch (error) {
      setResetMessage('');
      setLoginError("❌ Reset failed. Valid email?");
    }
  };

  return (
    <div className="h-screen w-full flex items-center justify-center bg-slate-50 font-sans selection:bg-indigo-100 p-4">
      <div className="bg-white p-6 sm:p-10 rounded-2xl shadow-xl border border-slate-200 w-full max-w-[420px]">
        
        {/* Branding */}
        <div className="flex flex-col items-center justify-center mb-6 sm:mb-8">
          <div className="bg-slate-900 p-3 rounded-xl shadow-lg mb-3 sm:mb-4">
            <Server size={20} className="text-white sm:w-6 sm:h-6"/>
          </div>
          <h2 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight uppercase">SaaS Engine</h2>
          <p className="text-slate-400 text-[9px] sm:text-[10px] font-bold mt-1 uppercase tracking-widest">Secure Access Portal</p>
        </div>
        
        {/* Error Messages */}
        {loginError && (
          <div className="bg-rose-50 border border-rose-100 text-rose-600 p-3 sm:p-4 rounded-xl text-[10px] sm:text-xs font-semibold mb-4 sm:mb-6 flex items-start gap-2">
            <Lock size={14} className="mt-0.5 flex-shrink-0" />
            <span>{loginError}</span>
          </div>
        )}

        {/* Success Message */}
        {resetMessage && (
          <div className="bg-emerald-50 border border-emerald-100 text-emerald-700 p-3 sm:p-4 rounded-xl text-[10px] sm:text-xs font-semibold mb-4 sm:mb-6 flex items-start gap-2">
            <Mail size={14} className="mt-0.5 flex-shrink-0" />
            <span>{resetMessage}</span>
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-4 sm:space-y-5">
          <div>
            <label className="block text-[9px] sm:text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5 sm:mb-2 ml-1">Work Email</label>
            <input 
              type="email" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 sm:px-5 sm:py-3.5 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:border-indigo-500 focus:bg-white transition-all text-xs sm:text-sm font-semibold text-slate-900"
              placeholder="admin@business.com"
              required
            />
          </div>
          <div>
            <div className="flex justify-between items-center mb-1.5 sm:mb-2 ml-1">
              <label className="block text-[9px] sm:text-[10px] font-black text-slate-500 uppercase tracking-widest">Password</label>
              <button 
                type="button" 
                onClick={handleForgotPassword}
                className="text-[9px] sm:text-[10px] font-bold text-indigo-600 hover:text-indigo-800 transition-colors focus:outline-none"
              >
                Forgot Password?
              </button>
            </div>
            <input 
              type="password" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 sm:px-5 sm:py-3.5 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:border-indigo-500 focus:bg-white transition-all text-xs sm:text-sm font-semibold text-slate-900 tracking-widest"
              placeholder="••••••••"
            />
          </div>
          
          <button 
            type="submit" 
            disabled={isLoading}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-black py-3 sm:py-4 rounded-xl transition-all shadow-md mt-4 sm:mt-6 flex items-center justify-center gap-2 text-[10px] sm:text-xs uppercase tracking-widest disabled:opacity-70"
          >
            {isLoading ? 'Authenticating...' : <>Access Dashboard <ArrowRight size={14} /></>}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;